<template>
  <v-container v-if="orgLoading" fluid class="organization-loading d-flex align-center justify-center">
    <v-card class="pa-6 text-center" variant="outlined" max-width="360">
      <v-progress-circular indeterminate size="42" width="3" color="primary" />
      <div class="text-body-2 text-medium-emphasis mt-3">Loading organization...</div>
    </v-card>
  </v-container>

  <v-container v-else fluid class="organization-page pa-0">
    <v-container class="organization-shell">
      <v-card v-if="orgOptions && orgOptions.length > 1" variant="outlined" class="pa-4 mb-5">
        <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between ga-3">
          <div>
            <div class="text-caption text-uppercase font-weight-bold text-primary mb-1">Multiple organizations</div>
            <p class="text-body-2 text-medium-emphasis mb-0">{{ $t("text.account_multiple_org_access") }}</p>
          </div>
          <DirectorySwitcherDialog />
        </div>
      </v-card>

      <section class="mb-5">
        <div class="mb-3 px-1">
          <div class="text-caption text-uppercase font-weight-bold text-primary mb-1">Overview</div>
          <h2 class="text-h5 mb-1">Company profile</h2>
          <p class="text-body-2 text-medium-emphasis mb-0">Registered company details and delivery information in one
            view.</p>
        </div>
        <OrganizationHeader :organization="organization" :loading="orgLoading" />
      </section>

      <v-row>
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="pa-3 h-100">
            <div class="mb-3 px-1">
              <div class="text-caption text-uppercase font-weight-bold text-primary mb-1">{{ $t("text.users") }}</div>
              <h2 class="text-h6 mb-1">Portal accounts</h2>
              <p class="text-body-2 text-medium-emphasis mb-0">Manage who can sign in and access this organization.</p>
            </div>
            <AccountList></AccountList>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card variant="outlined" class="pa-3 h-100">
            <div class="mb-3 px-1">
              <div class="text-caption text-uppercase font-weight-bold text-primary mb-1">{{ $t("text.contacts") }}
              </div>
              <h2 class="text-h6 mb-1">People and notifications</h2>
              <p class="text-body-2 text-medium-emphasis mb-0">Review the contacts attached to orders and customer
                communication.</p>
            </div>
            <ContactList></ContactList>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>


<script>
import http from "@/api/client";
import AccountList from "@/components/AccountList.vue";
import ContactList from "@/components/ContactList.vue";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";
import OrganizationHeader from "@/components/OrganizationHeader.vue";
import DirectorySwitcherDialog from "../../components/Dialogs/DirectorySwitcherDialog.vue";
import AccountButton from "@/components/AccountButton.vue";

export default {
  name: "Organization",
  mixins: [AuthStoreMixin],
  components: {
    OrganizationHeader,
    ContactList,
    AccountList,
    DirectorySwitcherDialog,
    AccountButton,
  },

  data() {
    return {
      organization: {},
      orgLoading: false,
    };
  },

  mounted() {
    this.getOrg();
  },

  methods: {
    async getOrg() {
      this.orgLoading = true;
      try {
        const response = await http().get("api2/customer/userinfo/organization");
        this.organization = response.data;
      }
      finally {
        this.orgLoading = false;
      }
    },
  },

  computed: {
    orgOptions() {
      if (this.authImpersonationOptions.length) {
        return this.authImpersonationOptions;
      }

      return this.authCustomer ? [this.authCustomer] : [];
    },

    organizationName() {
      return this.organization.Name || this.authCustomer?.Name || "Organization overview";
    },

    organizationNumber() {
      return this.organization.OrganisationNumber || this.$t("text.not_specified");
    },

    deliveryCity() {
      return this.organization.DeliveryAdressCity || this.organization.DeliveryAdressCountry || this.$t("text.not_specified");
    },

    deliveryCountry() {
      return this.organization.DeliveryAdressCountry || this.$t("text.not_specified");
    },

    deliveryAddress() {
      const addressParts = [
        this.organization.DeliveryAdressStreet,
        [this.organization.DeliveryAdressZip, this.organization.DeliveryAdressCity].filter(Boolean).join(" "),
        this.organization.DeliveryAdressCountry,
      ].filter(Boolean);

      return addressParts.length ? addressParts.join(", ") : this.$t("text.not_specified");
    },

    contactCount() {
      return Array.isArray(this.organization.CustomerPeople) ? this.organization.CustomerPeople.length : 0;
    },

    accessCount() {
      return this.orgOptions.length || 1;
    },

    accessCountLabel() {
      return `${this.accessCount} directories available`;
    },

    heroDescription() {
      if (this.deliveryAddress !== this.$t("text.not_specified")) {
        return `Keep company details, delivery information, and customer contacts aligned around ${this.deliveryAddress}.`;
      }

      return "Manage company information, account access, and customer contacts from one organized view.";
    },
  },

  watch: {},
};
</script>

<style scoped>
.organization-page {
  min-height: 100vh;
  background: #fafafa;
}

.organization-shell {
  max-width: 1360px;
  padding-top: 20px;
  padding-bottom: 32px;
}

.organization-loading {
  min-height: 100vh;
  background: #fafafa;
}

@media (max-width: 600px) {
  .organization-shell {
    padding-top: 12px;
    padding-bottom: 24px;
  }
}
</style>